import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import TokenWidget from "../TokenWidget";
import { useT } from "../../context/LanguageContext";
import {
  ChevronRight, Trophy, Users, Sparkles, Zap, Crown, Medal, Lock, Smartphone, Monitor, Globe2,
} from "lucide-react";

/**
 * V2 / Phase 2 — "Explore" tab.
 * Shows the user every available competition surface in one mobile-first feed:
 * Master Leagues (mobile + console), Classics (Parsec PES/FIFA), BYOL, Hall of Fame.
 */
export default function ExploreV2() {
  const { t } = useT();
  const [leagues, setLeagues] = useState([]);
  const [hof, setHof] = useState([]);
  const [byol, setByol] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: lg }, { data: hofData }, { data: byolData }] = await Promise.all([
          api.get("/leagues").catch(() => ({ data: [] })),
          api.get("/hall-of-fame").catch(() => ({ data: [] })),
          api.get("/byol/leagues/mine").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setLeagues(Array.isArray(lg) ? lg : []);
        setHof(Array.isArray(hofData) ? hofData : []);
        setByol(Array.isArray(byolData) ? byolData : []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group: open or live master leagues by platform.
  const mobileLeague = leagues.find((l) => l.platform === "mobile" && (l.status === "open" || l.status === "active"));
  const consoleLeague = leagues.find((l) => l.platform === "console" && (l.status === "open" || l.status === "active"));

  if (loading) {
    return <div className="p-6 text-center text-[#52525B] text-sm" data-testid="v2-explore-loading">{t("v2.shell.loading")}</div>;
  }

  return (
    <div className="space-y-6 px-5 pt-5 pb-2" data-testid="v2-explore">
      {/* Header */}
      <section data-testid="v2-explore-header">
        <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A5B4FC]">{t("v2.exp.kicker")}</div>
        <h1 className="text-2xl font-bold text-white mt-0.5">{t("v2.exp.title")}</h1>
        <p className="text-sm text-[#71717A] mt-1.5">{t("v2.exp.subtitle")}</p>
      </section>

      {/* World Cup 2026 — iter 85: official FIFA-branded banner artwork. */}
      <section data-testid="v2-explore-wc2026">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">FIFA World Cup 2026</h3>
        <Link
          to="/v2/wc2026"
          data-testid="v2-explore-wc2026-card"
          className="block group relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#FFD700]/60 hover:ring-[#FFD700] transition-shadow bg-[#020617]"
        >
          <img
            src="/wc/wc2026-banner.png"
            alt="FIFA World Cup 2026 — Explore new modes"
            loading="eager"
            decoding="async"
            className="block w-full h-auto select-none"
            draggable={false}
          />
        </Link>
      </section>

      {/* UFL Mobile — iter 88: new banner with players photo art, moved here
          from MyLeague. The artwork already embeds the design (UFL logo,
          bullets, TAP TO ENTER button) so we only overlay the dynamic LIVE
          pill (top-right) + the LIVE ROOM N/M status (bottom-left) on top. */}
      <UflMobileExploreBanner />

      {/* Token widget — earn now, spend when the store opens */}
      <TokenWidget />

      {/* Master Leagues */}
      <section data-testid="v2-explore-master">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">Master Leagues</h3>
        <div className="space-y-2.5">
          <LeagueCard
            kind="mobile"
            league={mobileLeague}
            label="Mobile · eFootball"
            description="Online Master League · 20 players · Double round-robin"
            icon={Smartphone}
            gradient="from-[#7C3AED] to-[#3B82F6]"
            badgeColor="#7C3AED"
            cover="/byol-cover.png"
          />
          <LeagueCard
            kind="console"
            league={consoleLeague}
            label="Console · PS5 / Steam"
            description="Online Master League · 20 players · Double round-robin"
            icon={Monitor}
            gradient="from-[#10B981] to-[#3B82F6]"
            badgeColor="#10B981"
            cover="/byol-cover.png"
          />
        </div>
      </section>

      {/* International Friendlies */}
      <section data-testid="v2-explore-friendlies">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">International Friendlies</h3>
        <Link
          to="/v2/friendlies"
          data-testid="v2-explore-friendlies-tile"
          className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#60A5FA]/50 hover:ring-[#60A5FA] transition-shadow group"
        >
          <div className="bg-gradient-to-br from-[#0c1f4d] via-[#1a3a8c] to-[#040a1e] h-44 flex items-center px-5 relative">
            <div className="text-7xl mr-4 group-hover:scale-110 transition-transform">🌍</div>
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#60A5FA] text-[#040a1e] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-[#040a1e]" /> Live
              </span>
              <h4 className="mt-2 text-lg font-extrabold text-white drop-shadow leading-tight">International Friendlies</h4>
              <p className="text-[11px] text-white/85 mt-1 drop-shadow">Random national-team draws · cross-platform pool · live chat</p>
            </div>
            <Globe2 className="absolute right-4 top-4 w-4 h-4 text-white/40" />
          </div>
        </Link>
      </section>

      {/* Co-op Matchmaking */}
      <section data-testid="v2-explore-coop">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">Find gaming buddies</h3>
        <Link
          to="/v2/coop"
          data-testid="v2-explore-coop-tile"
          className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#A78BFA]/50 hover:ring-[#A78BFA] transition-shadow group"
        >
          <div className="bg-gradient-to-br from-[#1a0a3e] via-[#2a0e6f] to-[#0a0420] h-44 flex items-center px-5 relative">
            <div className="text-7xl mr-4 group-hover:scale-110 transition-transform">🎮</div>
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#A78BFA] text-[#06061A] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-[#06061A]" /> New
              </span>
              <h4 className="mt-2 text-lg font-extrabold text-white drop-shadow leading-tight">Co-op Matchmaking</h4>
              <p className="text-[11px] text-white/85 mt-1 drop-shadow">AI-matched gaming buddies · swipe to connect</p>
            </div>
            <div className="absolute right-4 top-4 text-[10px] font-mono uppercase tracking-widest text-white/40">// AI</div>
          </div>
        </Link>
      </section>

      {/* Classics */}
      <section data-testid="v2-explore-proclubs">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">EA FC Pro Clubs · New Gen</h3>
        <Link
          to="/v2/proclubs"
          data-testid="v2-explore-proclubs-tile"
          className="block relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#00FF87]/60 hover:ring-[#00FF87] transition-shadow group"
        >
          <img
            src="/wc/proclubs.png"
            alt="EA FC Pro Clubs"
            className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06120A]/95 via-[#06120A]/40 to-transparent" />
          <span className="absolute top-3 right-3 v2-wc-soon-pulse inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-[#06120A]" /> Live
          </span>
          <div className="absolute inset-x-0 bottom-0 p-4">
            <h4 className="text-lg font-extrabold text-white drop-shadow leading-tight">Pro Clubs · 10-team Leagues</h4>
            <p className="text-[11px] text-white/85 mt-1 drop-shadow">Captain registers · invites up to 10 players · weekend fixtures · scorers per match</p>
            <span className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest w-fit shadow">
              Enter Pro Clubs →
            </span>
          </div>
        </Link>
      </section>

      {/* Classics */}
      <section data-testid="v2-explore-classics">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">Retro 1v1</h3>
        <Link
          to="/classics"
          data-testid="v2-explore-classics-tile"
          className="block relative overflow-hidden rounded-3xl shadow-2xl shadow-[#10B981]/20 group"
        >
          <img src="/classics-homepage.jpg" alt="Classics" className="w-full h-44 object-cover group-hover:scale-[1.02] transition-transform" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070710] via-[#070710]/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> Live · PC only
            </div>
            <h4 className="text-lg font-bold text-white mt-1">Classics — Retro PES & FIFA</h4>
            <p className="text-xs text-white/70 mt-0.5">PES 13–21 + FIFA 15–23 over Parsec · 1v1 nostalgia</p>
          </div>
        </Link>
      </section>

      {/* BYOL */}
      <section data-testid="v2-explore-byol">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-2.5">Private leagues</h3>
        <Link
          to="/byol"
          data-testid="v2-explore-byol-tile"
          className="block relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F59E0B] via-[#EA580C] to-[#7C3AED] p-5 shadow-2xl shadow-[#F59E0B]/20"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">Build Your Own</span>
              <Sparkles className="w-4 h-4 text-white/80" />
            </div>
            <h4 className="text-xl font-bold text-white mt-1.5">Build Your Own League</h4>
            <p className="text-xs text-white/80 mt-1">Invite-only · Custom platform · 4–32 players</p>
            {byol.length > 0 ? (
              <div className="mt-3 inline-flex items-center gap-1 bg-white/15 backdrop-blur rounded-full px-3 py-1.5 text-[11px] font-semibold text-white">
                <Trophy className="w-3 h-3" /> {byol.length} league{byol.length !== 1 ? "s" : ""} created by you
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-white">
                Create one → <ChevronRight className="w-3 h-3" />
              </div>
            )}
          </div>
        </Link>
      </section>

      {/* Hall of Fame */}
      {hof.length > 0 && (
        <section data-testid="v2-explore-hof">
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Hall of Fame</h3>
            <Link to="/social" className="text-xs font-medium text-[#A5B4FC] hover:text-white flex items-center gap-0.5">
              See all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-1.5">
            {hof.slice(0, 5).map((p, idx) => (
              <HofRow key={p.user_id || p.username} player={p} rank={idx + 1} />
            ))}
          </div>
        </section>
      )}

      <div className="h-2" />
    </div>
  );
}

function LeagueCard({ kind, league, label, description, icon: Icon, gradient, badgeColor, cover }) {
  const isLive = !!league;
  const status = league?.status;
  const fullness = league ? Math.round((league.player_count / league.capacity) * 100) : 0;
  return (
    <Link
      to={league ? `/v2/leagues/${league.id}` : "#"}
      data-testid={`v2-explore-master-${kind}`}
      className={`relative block overflow-hidden rounded-3xl border ${isLive ? "border-[#27272F] bg-[#12121C] hover:bg-[#161620]" : "border-dashed border-[#27272F] bg-[#0F0F18]"} transition-colors`}
    >
      <div className="flex items-stretch">
        <div className="relative w-24 flex-shrink-0">
          <img src={cover} alt={label} className="w-24 h-full object-cover" />
          <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-50`} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="w-8 h-8 text-white drop-shadow-lg" />
          </div>
        </div>
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-center gap-2">
            {isLive ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: badgeColor }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: badgeColor }} /> Live · {status}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.25em] text-[#52525B]">
                <Lock className="w-2.5 h-2.5" /> Coming soon
              </span>
            )}
          </div>
          <h4 className="text-sm font-bold text-white mt-1 truncate">{label}</h4>
          <p className="text-[11px] text-[#71717A] mt-0.5 leading-tight">{description}</p>
          {isLive && (
            <div className="mt-2.5">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="text-[#A1A1AA] font-semibold">{league.player_count}/{league.capacity}</span>
                <span className="text-[#71717A]">{fullness}% filled</span>
              </div>
              <div className="h-1 bg-[#1F1F2A] rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${gradient} rounded-full`} style={{ width: `${fullness}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function HofRow({ player, rank }) {
  const RankIcon = rank === 1 ? Crown : rank === 2 ? Trophy : rank === 3 ? Medal : null;
  const rankColor = rank === 1 ? "#FFD700" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : "#71717A";
  return (
    <Link
      to={`/v2/players/${player.username}`}
      data-testid={`v2-hof-row-${rank}`}
      className="flex items-center gap-3 bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl px-3 py-2.5 transition-colors"
    >
      <div className="w-8 flex items-center justify-center">
        {RankIcon ? (
          <RankIcon className="w-4 h-4" style={{ color: rankColor }} />
        ) : (
          <span className="text-xs font-bold text-[#71717A]">{rank}</span>
        )}
      </div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
        {(player.username || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">{player.username}</div>
        {player.club_name && <div className="text-[11px] text-[#71717A] truncate">{player.club_name}</div>}
      </div>
      <div className="text-right">
        <div className="text-sm font-bold text-white">{player.points ?? 0}</div>
        <div className="text-[9px] uppercase tracking-wider text-[#71717A]">pts</div>
      </div>
    </Link>
  );
}


/* -------------------------------------------------------------------------- */
/* iter 88 — UFL Mobile banner on the Explore page.
   The artwork (/wc/ufl-mobile-banner.png) embeds the FULL design (UFL logo,
   players, bullets, TAP TO ENTER button, LIVE pill, LIVE ROOM badge). We
   render it pixel-faithful — clicking anywhere on the banner navigates into
   the UFL mobile hub where live room state is the source of truth. */
function UflMobileExploreBanner() {
  return (
    <section data-testid="v2-explore-ufl-mobile">
      <Link
        to="/v2/ufl/mobile"
        data-testid="v2-explore-ufl-mobile-card"
        className="block group relative overflow-hidden rounded-3xl shadow-2xl ring-2 ring-[#60A5FA]/40 hover:ring-[#60A5FA] transition-shadow bg-[#020617]"
      >
        <img
          src="/wc/ufl-mobile-banner.jpg"
          alt="UFL Mobile — Quick Play 4-player rooms. Create or join teams."
          loading="eager"
          decoding="async"
          className="block w-full h-auto select-none"
          draggable={false}
        />
      </Link>
    </section>
  );
}
